// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, WrappedComponentProps, injectIntl, FormattedMessage } from "react-intl";
import { OptionProps, components } from "react-select";

import { DashboardPanel } from "types";
import { isSet } from "utils";
import { IState } from "states/states";
import { IAction, ActionType, raiseAction, IActionTypeSelectDashboardItem } from "actions";
import { IEnergyMeter, ITariffCircuit } from "parameter";
import { IPanelSelection } from "states/cachedstates";
import { allOption, IDropdownSelectOption, SELECT_ALL_OPTION_ITEMS } from "./DropdownSelect";

import DropdownSelect from "view/comp/DropdownSelect";
import TariffCircuitListModel from "model/tariffcircuitlist";
import { getColor, getPanelSelection } from "guiutils";

interface ITariffCircuitSelectExtProps {
  allowAllEnergySources: boolean;
  allowAllHierarchies: boolean;
  ignoreCategories: boolean;
  className?: string;
  dashboardIndex?: number;
  panel: DashboardPanel;
}

interface ITariffCircuitSelectProps {
  tariffId: number;
  tariffCircuits: ITariffCircuit[];
  energyMeters: IEnergyMeter[];
  selection: IPanelSelection | undefined;
  selectedDashboardIndex: number;
}

const mapStateToProps = (state: IState, ownProps: ITariffCircuitSelectExtProps) => {
  const { index, selection } = getPanelSelection(state, ownProps.panel, ownProps.dashboardIndex);
  const props: ITariffCircuitSelectProps = {
    tariffId: state.gui.tariffId,
    tariffCircuits: state.parameter?.tariffline ?? [],
    energyMeters: state.parameter?.energymeter ?? [],
    selectedDashboardIndex: index,
    selection,
  };
  return props;
};

interface ITariffCircuitSelectDispatch {
  onDropdownIOSelectionChanged: (index: number, selectedIds: number[]) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITariffCircuitSelectDispatch = {
    onDropdownIOSelectionChanged: (index: number, selectedIds: number[]) => {
      const payload: IActionTypeSelectDashboardItem = { index, selectedIds };
      dispatch(raiseAction(ActionType.ACT_SELECT_TARIFFCIRCUITS, payload));
    },
  };
  return dispatchProps;
};

export function getAvailableTariffsFromSource(tariffCircuits: ITariffCircuit[]) {
  const tariffIds = Array.from(Array(tariffCircuits.length).keys())
    .filter((curId) => tariffCircuits[curId].webActive)
    .sort((a, b) => tariffCircuits[a].name.localeCompare(tariffCircuits[b].name));
  const allOptions: IDropdownSelectOption[] = tariffIds.map((anId) => {
    return {
      value: "tariff" + String(anId),
      index: anId,
      label: tariffCircuits[anId].name,
      visible: true,
    } as IDropdownSelectOption;
  });
  return allOptions;
}

const messages = defineMessages({
  msg_user_caption_all: {
    id: "All tariff circuits",
  },
  msg_item_all: {
    id: "all",
  },
  selectionSomeItemsSelected: {
    id: "items selected",
  },
});

class TariffCircuitSelectComponent extends React.Component<
  ITariffCircuitSelectExtProps &
    ITariffCircuitSelectProps &
    ITariffCircuitSelectDispatch &
    WrappedComponentProps
> {
  private optionsCallback(props: OptionProps<IDropdownSelectOption, boolean, any>) {
    return (
      <div
        style={{
          backgroundColor: "rgba(0,0,0,0)",
        }}
      >
        <components.Option {...props}>
          <input
            style={{
              accentColor:
                props.data.index === SELECT_ALL_OPTION_ITEMS
                  ? "rgba(1,1,1,1)"
                  : getColor(props.data.index),
            }}
            type="checkbox"
            checked={props.isSelected}
            onChange={() => null}
          />{" "}
          <label
            style={{
              color:
                props.data.index === SELECT_ALL_OPTION_ITEMS
                  ? "rgba(1,1,1,1)"
                  : getColor(props.data.index),
            }}
          >
            {props.data.index !== SELECT_ALL_OPTION_ITEMS
              ? props.data.label
              : this.props.intl.formatMessage({ id: props.data.label as string })}
          </label>
        </components.Option>
      </div>
    );
  }

  public render() {
    const showAllChildren = false;
    const tcModel = new TariffCircuitListModel(this.props.tariffCircuits, this.props.energyMeters);
    const tariffCircuitIndexes = tcModel.getTariffCircuits(
      this.props.selection?.energySource,
      this.props.allowAllEnergySources,
      this.props.selection?.hierarchyId,
      this.props.allowAllHierarchies,
      this.props.ignoreCategories ? undefined : this.props.selection?.category,
      showAllChildren,
    );
    const allOptions = tcModel.makeOptionsArray(tariffCircuitIndexes, undefined, showAllChildren);
    const groupedOptions = [
      { label: <FormattedMessage id="Select tariff circuit" />, options: allOptions },
    ];
    const selectedTariffCircuitIds = this.props.selection?.tariffCircuitIds ?? [];
    const selectedOptions = tcModel.makeOptionsArray(
      tariffCircuitIndexes,
      this.props.selection?.tariffCircuitIds,
      showAllChildren,
    );

    const isAllSelected =
      !isSet(this.props.selection?.tariffCircuitIds) ||
      tariffCircuitIndexes.length === selectedTariffCircuitIds.length;
    if (isAllSelected) {
      selectedOptions.push(allOption);
    }

    return (
      <div id="tariffcircuitselect" className={this.props.className}>
        <DropdownSelect
          options={groupedOptions}
          noOptionsString={<FormattedMessage id="No tariff available" />}
          components={{ Option: (props) => this.optionsCallback(props) }}
          isMulti={true}
          closeMenuOnSelect={false}
          value={selectedOptions.filter((curItem) => curItem.visible)}
          placeholder={
            <div>
              {allOptions.length > 0 ? (
                isAllSelected ? (
                  <FormattedMessage id="All tariff circuits" />
                ) : selectedTariffCircuitIds.length > 1 ? (
                  String(selectedTariffCircuitIds.length) +
                  " " +
                  this.props.intl.formatMessage(messages.selectionSomeItemsSelected)
                ) : selectedTariffCircuitIds.length === 1 ? (
                  this.props.tariffCircuits.find(
                    (curItem, curIndex) => (curItem.id ?? curIndex) === selectedTariffCircuitIds[0],
                  )?.name
                ) : (
                  <FormattedMessage id="Select tariff circuit" />
                )
              ) : (
                <FormattedMessage id="No tariff available" />
              )}
            </div>
          }
          onChange={(newValue: IDropdownSelectOption[]) =>
            this.props.onDropdownIOSelectionChanged(
              this.props.selectedDashboardIndex,
              newValue
                .map((curItem) => curItem.id)
                .filter((curId) => curId !== SELECT_ALL_OPTION_ITEMS),
            )
          }
          allowSelectAll={true}
        />
      </div>
    );
  }
}

const TariffCircuitSelect = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TariffCircuitSelectComponent);

export default injectIntl(TariffCircuitSelect);
